import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  BuildingStorefrontIcon,
  CheckCircleIcon,
  XCircleIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/api';
import toast from 'react-hot-toast';

interface PageAccessInfo {
  path: string;
  name: string;
  description: string;
  category: string;
  enabled: boolean;
  configField: string | null;
}

interface VendorPageAccessResponse {
  vendorId: number;
  username: string;
  email: string;
  name: string;
  businessType: string;
  simpleMode: boolean;
  pages: PageAccessInfo[];
}

const VendorPageAccess: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const { theme } = useTheme();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendorData, setVendorData] = useState<VendorPageAccessResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Access denied: Admin privileges required');
      navigate('/');
      return;
    }

    if (!vendorId) {
      setError('Vendor ID is required');
      setLoading(false);
      return;
    }

    fetchVendorPageAccess();
  }, [vendorId, isAdmin, navigate]);

  const fetchVendorPageAccess = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/admin/vendors/${vendorId}/page-access`);
      setVendorData(response.data);
    } catch (err: any) {
      console.error('Error fetching vendor page access:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load vendor page access data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChange = (configField: string, currentValue: boolean) => {
    if (!vendorData) return;

    const updatedPages = vendorData.pages.map((page) =>
      page.configField === configField ? { ...page, enabled: !currentValue } : page
    );

    setVendorData({ ...vendorData, pages: updatedPages });
    setHasChanges(true);
  };

  const handleBusinessTypeChange = (businessType: string) => {
    if (!vendorData) return;
    setVendorData({ ...vendorData, businessType });
    setHasChanges(true);
  };

  const handleSimpleModeChange = (simpleMode: boolean) => {
    if (!vendorData) return;
    setVendorData({ ...vendorData, simpleMode });
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!vendorData) return;

    try {
      setSaving(true);

      // Build update payload from changed pages
      const updates: Record<string, any> = {
        businessType: vendorData.businessType,
        simpleMode: vendorData.simpleMode,
      };

      // Add all configurable page access fields
      vendorData.pages.forEach((page) => {
        if (page.configField) {
          updates[page.configField] = page.enabled;
        }
      });

      await api.put(`/api/admin/vendors/${vendorId}/configuration`, updates);

      toast.success('Vendor configuration updated successfully');
      setHasChanges(false);

      // Refresh data to get latest state
      await fetchVendorPageAccess();
    } catch (err: any) {
      console.error('Error updating vendor configuration:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update vendor configuration';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    fetchVendorPageAccess();
    setHasChanges(false);
  };

  // Group pages by category
  const pagesByCategory = vendorData?.pages.reduce((acc, page) => {
    if (!acc[page.category]) {
      acc[page.category] = [];
    }
    acc[page.category].push(page);
    return acc;
  }, {} as Record<string, PageAccessInfo[]>) || {};

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !vendorData) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Failed to load vendor data'}</Alert>
        <Button onClick={() => navigate('/admin/users')} sx={{ mt: 2 }}>
          Back to User Management
        </Button>
      </Box>
    );
  }

  // Debug log to verify component is rendering
  console.log('VendorPageAccess component rendering for vendor:', vendorId);
  console.log('vendorData:', vendorData);
  console.log('pagesByCategory:', pagesByCategory);

  return (
    <Box sx={{ p: 3 }} data-component="vendor-page-access">
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: theme.primary, width: { xs: 40, sm: 56 }, height: { xs: 40, sm: 56 } }}>
            <BuildingStorefrontIcon className="w-8 h-8" />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Vendor Page Access
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {vendorData.name} ({vendorData.username})
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          {hasChanges && (
            <>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
                size="small"
                sx={{
                  backgroundColor: 'transparent !important',
                  color: (theme.gray?.[700] || '#374151') + ' !important',
                  border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
                  fontSize: '0.875rem',
                  padding: '8px 16px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  textTransform: 'none',
                  boxShadow: 'none !important',
                  '&:hover': {
                    backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important',
                    borderColor: (theme.gray?.[400] || '#9ca3af') + ' !important',
                    boxShadow: 'none !important'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveChanges}
                disabled={saving}
                size="small"
                startIcon={saving && <CircularProgress size={20} sx={{ color: theme.accent + ' !important' }} />}
                sx={{
                  backgroundColor: theme.primary + ' !important',
                  color: 'var(--text-inverse) !important',
                  border: 'none !important',
                  fontSize: '0.875rem',
                  padding: '8px 16px',
                  fontWeight: 500,
                  borderRadius: '8px',
                  textTransform: 'none',
                  boxShadow: 'none !important',
                  '&:hover': {
                    backgroundColor: (theme.primaryHover || theme.primary) + ' !important',
                    boxShadow: 'none !important'
                  },
                  '&:disabled': {
                    backgroundColor: 'var(--bg-sunken) !important',
                    color: 'var(--text-tertiary) !important',
                    border: '1px solid var(--border-subtle) !important'
                  }
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Vendor Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Vendor Configuration
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Email
              </Typography>
              <Typography variant="body1">{vendorData.email}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={vendorData.businessType}
                  onChange={(e) => handleBusinessTypeChange(e.target.value)}
                  label="Business Type"
                >
                  <MenuItem value="FULL_SERVICE">Full Service Restaurant</MenuItem>
                  <MenuItem value="QUICK_SERVICE">Quick Service</MenuItem>
                  <MenuItem value="CAFE">Cafe</MenuItem>
                  <MenuItem value="TAKEAWAY_ONLY">Takeaway Only</MenuItem>
                  <MenuItem value="CLOUD_KITCHEN">Cloud Kitchen</MenuItem>
                  <MenuItem value="FOOD_TRUCK">Food Truck</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={vendorData.simpleMode}
                    onChange={(e) => handleSimpleModeChange(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Simple Mode</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Simplified UI with essential features only (disables advanced table/floor management)
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Page Access by Category */}
      {Object.entries(pagesByCategory).map(([category, pages]) => (
        <Card key={category} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
              <Cog6ToothIcon className="w-5 h-5" />
              {category}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <List>
              {pages.map((page, index) => (
                <React.Fragment key={page.path}>
                  <ListItem
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      py: 2,
                    }}
                  >
                    <Box flex={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="body1" fontWeight="medium">
                          {page.name}
                        </Typography>
                        {page.enabled ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-500" />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {page.description}
                      </Typography>
                      <Chip
                        label={page.path}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 1 }}
                        icon={<DocumentTextIcon className="w-4 h-4" />}
                      />
                    </Box>
                    <Box>
                      {page.configField ? (
                        <Switch
                          checked={page.enabled}
                          onChange={() => handleToggleChange(page.configField!, page.enabled)}
                          color="primary"
                        />
                      ) : (
                        <Chip label="Always Enabled" size="small" color="success" />
                      )}
                    </Box>
                  </ListItem>
                  {index < pages.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default VendorPageAccess;
